import { TFunction } from 'i18next';
import {
  ExternalLink,
  FileSignature,
  FileText,
  Pencil,
  Lock,
  LockOpen,
  ListChecks,
  ArrowLeftRight,
  CloudUpload,
  CloudOff,
  Archive,
  Search,
  Plus,
  MessageSquare,
} from 'lucide-react';

import { PATHS } from 'router/config';
import { PopupNames } from 'core';
import { PaletteEntity } from 'core/state';
import { UserRole } from 'types';
import {
  IStudentPermissions,
  visibleFor,
  STUDENT_ROLES,
  EDUCATION_ROLES,
  ADMIN_ROLES,
} from 'core/hooks';

import { IAction } from './paletteTypes';

/** Everything the action registry needs to gate + label entity actions. */
export interface IActionGate {
  roles: UserRole[];
  permissions: IStudentPermissions;
  t: TFunction;
}

const label = (t: TFunction, id: string): string =>
  t(`commandPalette.actions.${id}`);

/**
 * The primary «open» action for an entity — what `Enter` runs while the entity
 * is highlighted in the search list. Courses have no detail card, so their
 * primary seeds the courses-list search box and navigates there.
 */
export const openActionFor = (entity: PaletteEntity, t: TFunction): IAction => {
  switch (entity.kind) {
    case 'student':
      return {
        id: 'openStudent',
        title: label(t, 'openStudent'),
        icon: ExternalLink,
        run: (ctx) =>
          ctx.history.push(PATHS.studentDetail.replace(':id', entity.id)),
      };
    case 'group':
      return {
        id: 'openGroup',
        title: label(t, 'openGroup'),
        icon: ExternalLink,
        run: (ctx) =>
          ctx.history.push(PATHS.groupDetail.replace(':id', entity.id)),
      };
    case 'teacher':
      return {
        id: 'openTeacher',
        title: label(t, 'openTeacher'),
        icon: ExternalLink,
        run: (ctx) =>
          ctx.history.push(PATHS.teacherDetail.replace(':id', entity.id)),
      };
    case 'user':
      return {
        id: 'editUser',
        title: label(t, 'editUser'),
        icon: Pencil,
        run: (ctx) => ctx.showPopup(PopupNames.editUser, entity.id),
      };
    case 'course':
    default:
      return {
        id: 'openCourse',
        title: label(t, 'openCourse'),
        icon: Search,
        run: (ctx) => {
          ctx.setSettingsSearch(entity.title);
          ctx.history.push(PATHS.settingsCourses);
        },
      };
  }
};

const studentActions = (
  entity: Extract<PaletteEntity, { kind: 'student' }>,
  gate: IActionGate,
): IAction[] => {
  const { permissions, roles, t } = gate;
  const actions: IAction[] = [openActionFor(entity, t)];

  // «Open conversation» — jump to the communicator. Gated by the same roles
  // that can reach /communicator (STUDENT_ROLES mirrors AppRouter). Placed
  // right after the primary open so it stays visible in the inline strip.
  if (visibleFor(roles, STUDENT_ROLES)) {
    actions.push({
      id: 'openConversation',
      title: label(t, 'openConversation'),
      icon: MessageSquare,
      run: (ctx) => ctx.history.push(PATHS.communicator),
    });
  }

  if (permissions.canAddAgreement) {
    actions.push({
      id: 'addAgreement',
      title: label(t, 'addAgreement'),
      icon: FileSignature,
      run: (ctx) =>
        ctx.showPopup(PopupNames.studentPayment, { studentId: entity.id }),
    });
    if (entity.agreement) {
      const { agreement } = entity;
      actions.push({
        id: 'editAgreement',
        title: label(t, 'editAgreement'),
        icon: Pencil,
        run: (ctx) =>
          ctx.showPopup(PopupNames.editAgreement, {
            agreementId: agreement.id,
            studentId: entity.id,
          }),
      });
    }
  }

  if (permissions.canBlockLmsAccess) {
    actions.push({
      id: entity.isLmsBlocked ? 'lmsUnblock' : 'lmsBlock',
      title: label(t, entity.isLmsBlocked ? 'lmsUnblock' : 'lmsBlock'),
      icon: entity.isLmsBlocked ? LockOpen : Lock,
      run: (ctx) =>
        ctx.showPopup(PopupNames.lmsBlockPopup, {
          studentId: entity.id,
          mode: entity.isLmsBlocked ? 'unblock' : 'block',
        }),
    });
  }

  if (visibleFor(roles, STUDENT_ROLES)) {
    actions.push({
      id: 'newTask',
      title: label(t, 'newTask'),
      icon: ListChecks,
      run: (ctx) =>
        ctx.showPopup(PopupNames.taskCreatePopup, { studentId: entity.id }),
    });
  }

  if (permissions.canChangeGroup && entity.agreement) {
    const { agreement } = entity;
    actions.push({
      id: 'moveToGroup',
      title: label(t, 'moveToGroup'),
      icon: ArrowLeftRight,
      run: (ctx) =>
        ctx.showPopup(PopupNames.moveToGroupPopup, {
          studentId: entity.id,
          agreement,
        }),
    });
  }

  return actions;
};

const groupActions = (
  entity: Extract<PaletteEntity, { kind: 'group' }>,
  gate: IActionGate,
): IAction[] => {
  const { roles, t } = gate;
  const actions: IAction[] = [openActionFor(entity, t)];

  // «Open group conversation» — jump to the communicator. STUDENT_ROLES gate
  // mirrors the /communicator route, so a group-seeing HRManager (who has no
  // communicator route) does not get this action.
  if (visibleFor(roles, STUDENT_ROLES)) {
    actions.push({
      id: 'openGroupConversation',
      title: label(t, 'openGroupConversation'),
      icon: MessageSquare,
      run: (ctx) => ctx.history.push(PATHS.communicator),
    });
  }

  if (visibleFor(roles, EDUCATION_ROLES)) {
    actions.push({
      id: 'editGroup',
      title: label(t, 'editGroup'),
      icon: Pencil,
      run: (ctx) => ctx.showPopup(PopupNames.editGroupPopup, entity.id),
    });
    actions.push({
      id: entity.publishedLms ? 'unpublishLms' : 'publishLms',
      title: label(t, entity.publishedLms ? 'unpublishLms' : 'publishLms'),
      icon: entity.publishedLms ? CloudOff : CloudUpload,
      run: (ctx) =>
        ctx.showPopup(
          entity.publishedLms
            ? PopupNames.removeGroupFromLMS
            : PopupNames.addGroupInLMS,
          entity.group,
        ),
    });
    actions.push({
      id: 'deleteGroup',
      title: label(t, 'deleteGroup'),
      icon: Archive,
      run: (ctx) =>
        ctx.showPopup(PopupNames.deleteGroup, { groupId: entity.id }),
    });
  }

  if (visibleFor(roles, STUDENT_ROLES)) {
    actions.push({
      id: 'newTask',
      title: label(t, 'newTask'),
      icon: ListChecks,
      run: (ctx) =>
        ctx.showPopup(PopupNames.taskCreatePopup, { groupId: entity.id }),
    });
  }

  return actions;
};

const courseActions = (
  entity: Extract<PaletteEntity, { kind: 'course' }>,
  gate: IActionGate,
): IAction[] => {
  const { roles, t } = gate;
  const actions: IAction[] = [openActionFor(entity, t)];

  if (visibleFor(roles, EDUCATION_ROLES)) {
    actions.push({
      id: 'createGroup',
      title: label(t, 'createGroup'),
      icon: Plus,
      run: (ctx) =>
        ctx.showPopup(PopupNames.addGroup, { prefillCourseId: entity.id }),
    });
  }

  return actions;
};

const teacherActions = (
  entity: Extract<PaletteEntity, { kind: 'teacher' }>,
  gate: IActionGate,
): IAction[] => {
  const { roles, t } = gate;
  const actions: IAction[] = [openActionFor(entity, t)];

  if (visibleFor(roles, EDUCATION_ROLES)) {
    actions.push({
      id: 'editTeacher',
      title: label(t, 'editTeacher'),
      icon: Pencil,
      run: (ctx) =>
        ctx.showPopup(PopupNames.teacherEditPopup, { teacherId: entity.id }),
    });
    actions.push({
      id: 'addApplication',
      title: label(t, 'addApplication'),
      icon: FileText,
      run: (ctx) =>
        ctx.showPopup(PopupNames.appendApplicationPopup, {
          teacherId: entity.id,
        }),
    });
  }

  return actions;
};

const userActions = (
  entity: Extract<PaletteEntity, { kind: 'user' }>,
  gate: IActionGate,
): IAction[] => {
  const { roles, t } = gate;
  if (!visibleFor(roles, ADMIN_ROLES)) return [];
  return [openActionFor(entity, t)];
};

/**
 * Build the drill-in action list for an entity, gated by role + student
 * permissions. Index `0` is always the primary «open» action, so `Enter` in the
 * search list and `Enter` on the first drill-in row do the same thing.
 */
export const buildEntityActions = (
  entity: PaletteEntity,
  gate: IActionGate,
): IAction[] => {
  switch (entity.kind) {
    case 'student':
      return studentActions(entity, gate);
    case 'group':
      return groupActions(entity, gate);
    case 'course':
      return courseActions(entity, gate);
    case 'teacher':
      return teacherActions(entity, gate);
    case 'user':
      return userActions(entity, gate);
    default:
      return [];
  }
};
